import { useCallback, useContext } from 'react'

import { uuid } from '@sanity/uuid'

import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import type { ValuationTypes } from '~/data/valuationTypes'

import {
  PushInteractionLocation,
  PushInteractionType,
  type PushProperties,
} from '../utils/posthogEvents'

import useAuthFormAnalytics from './useAuthFormAnalytics'
import useMabPostHog from './useMabPostHog'

/** Analytics step names for the drawer's own steps (FormBuilder owns the rest) */
export const ValuationJourneyStepNames: Record<string, string> = {
  artwork: 'Artwork Selection',
  type: 'Valuation Type Selection',
  auth: 'Login/Signup Drawer',
}

export interface StartJourneyAnalyticsArgs {
  /** Reuse an existing id (e.g. SSO resume) instead of minting a new one */
  formLaunchId?: string
  artistName?: string
  artistId?: string
  artworkName?: string
  artworkId?: string
  valuationType?: ValuationTypes
  /** Defaults to the journey drawer; the insurance modal passes its own */
  interactionLocation?: PushInteractionLocation
}

/**
 * PostHog tagging for the unified valuations journey drawer.
 *
 * One `formLaunchId` is minted when the journey starts (hero CTA click) and
 * kept in `state.formAnalytics` for every branch - switching between the
 * three valuation types only updates `valuationType`, never the id. The
 * embedded FormBuilder (expert), the `/valuation` page (instant) and the auth
 * drawer all spread this state, so their events carry the same id + tag.
 */
const useValuationJourneyAnalytics = () => {
  const { state, stateDispatch } = useContext(GlobalStoreContext)
  const { pushEvent } = useMabPostHog()
  const { setAnalyticsProperties } = useAuthFormAnalytics()

  const startJourneyAnalytics = useCallback(
    ({
      formLaunchId,
      artistName,
      artistId,
      artworkName,
      artworkId,
      valuationType,
      interactionLocation = PushInteractionLocation.ValuationJourneyDrawer,
    }: StartJourneyAnalyticsArgs = {}) => {
      const launchId = formLaunchId ?? uuid()

      stateDispatch({
        type: GlobalStoreProps.UPDATE_FORM_ANALYTICS_STATE,
        payload: {
          formLaunchId: launchId,
          formType: FormTypes.valuation,
          webProduct: FormTypes.valuation,
          interactionLocation,
          // Journey steps set their own number explicitly (artwork = 1, type = 2,
          // auth = 3) and FormBuilder increments from the last one
          formStepNumber: 0,
          artistName,
          artistId,
          artworkName,
          artworkId,
          valuationType,
        },
      })

      return launchId
    },
    [stateDispatch]
  )

  /**
   * Drawer-owned step loaded. Sets the number explicitly (deterministic on
   * back/forward) and fires the same `Form Step Loaded` event FormBuilder
   * emits so the whole journey reads as one series in PostHog.
   */
  const trackJourneyStepLoaded = useCallback(
    (stepName: string, stepNumber: number) => {
      if (!state.formAnalytics?.formLaunchId) return

      const payload: PushProperties = {
        ...state.formAnalytics,
        formStepName: stepName,
        formStepNumber: stepNumber,
        interactionType: PushInteractionType.FormLoad,
      }

      pushEvent('Form Step Loaded', payload)

      stateDispatch({
        type: GlobalStoreProps.UPDATE_FORM_ANALYTICS_STATE,
        payload: {
          ...state.formAnalytics,
          formStepNumber: stepNumber,
        },
      })
    },
    [pushEvent, state.formAnalytics, stateDispatch]
  )

  /**
   * Tags the journey with the chosen valuation type. Re-selecting a different
   * card just overwrites the tag - the `formLaunchId` is untouched.
   */
  const tagValuationType = useCallback(
    (valuationType: ValuationTypes) => {
      if (!state.formAnalytics?.formLaunchId) return

      const nextState = {
        ...state.formAnalytics,
        valuationType,
      }

      stateDispatch({
        type: GlobalStoreProps.UPDATE_FORM_ANALYTICS_STATE,
        payload: nextState,
      })

      pushEvent('Valuation Type Selected', {
        ...nextState,
        interactionType: PushInteractionType.Click,
        fieldSelection: valuationType,
      } as PushProperties)
    },
    [pushEvent, state.formAnalytics, stateDispatch]
  )

  /**
   * Copies the journey id + tag onto the auth analytics state so the
   * login/signup/SSO events fired from the embedded AuthDialog carry them.
   */
  const syncAuthAnalytics = useCallback(() => {
    if (!state.formAnalytics?.formLaunchId) return

    setAnalyticsProperties({
      formLaunchId: state.formAnalytics.formLaunchId,
      formType: state.formAnalytics.formType,
      valuationType: state.formAnalytics.valuationType,
    })
  }, [setAnalyticsProperties, state.formAnalytics])

  return {
    startJourneyAnalytics,
    trackJourneyStepLoaded,
    tagValuationType,
    syncAuthAnalytics,
  }
}

export default useValuationJourneyAnalytics
